import React, { useCallback, useEffect, useMemo, useState } from "react"
import { Table, Avatar, Space, Spin, ConfigProvider, Button } from "antd"
import { useNavigate } from "react-router-dom"
import dayjs from "dayjs"
import { Analytics } from "../../Apis/analyticsApi"

const DEFAULT_PAGE_SIZE = 10

const parseLoginAnalyticsPayload = (analytics) => {
  if (!analytics) {
    return { users: [], total: 0, pageSize: DEFAULT_PAGE_SIZE }
  }

  const usersField = analytics.users

  if (Array.isArray(usersField)) {
    return {
      users: usersField,
      total:
        analytics.count ??
        analytics.total_count ??
        analytics.users_count ??
        usersField.length,
      pageSize: analytics.page_size ?? DEFAULT_PAGE_SIZE,
    }
  }

  if (usersField && typeof usersField === "object") {
    const list = usersField.results ?? usersField.data ?? []
    return {
      users: Array.isArray(list) ? list : [],
      total:
        usersField.count ??
        analytics.count ??
        analytics.total_count ??
        (Array.isArray(list) ? list.length : 0),
      pageSize:
        usersField.page_size ?? analytics.page_size ?? DEFAULT_PAGE_SIZE,
    }
  }

  return { users: [], total: 0, pageSize: DEFAULT_PAGE_SIZE }
}

const AdminLoginAnalytics = ({ filterState }) => {
  const lang = localStorage.getItem("language") || "ar"
  const navigate = useNavigate()

  const [page, setPage] = useState(1)
  const [loading, setLoading] = useState(false)
  const [analytics, setAnalytics] = useState(null)

  const { users, total, pageSize } = useMemo(
    () => parseLoginAnalyticsPayload(analytics),
    [analytics],
  )

  const fetchLoginAnalytics = useCallback(
    async (pageToLoad = 1) => {
      if (!filterState) return

      const { startDate, endDate, filterType, country } = filterState
      const typeToSend = filterType === "tilldate" ? "customdate" : filterType

      setLoading(true)
      try {
        const response = await Analytics.getUserPaymentSummary(
          startDate,
          endDate,
          typeToSend,
          country || [],
          pageToLoad,
        )
        if (response.status === 200) {
          setAnalytics(response.data?.admin_login_analytics ?? null)
        }
      } catch (error) {
        console.error("Error fetching login analytics:", error)
        setAnalytics(null)
      } finally {
        setLoading(false)
      }
    },
    [filterState],
  )

  useEffect(() => {
    setPage(1)
  }, [filterState])

  useEffect(() => {
    fetchLoginAnalytics(page)
  }, [page, fetchLoginAnalytics])

  const tableData = useMemo(() => {
    return users.map((user) => {
      const displayName =
        lang === "ar"
          ? user.name_arabic || user.name_english
          : user.name_english || user.name_arabic
      return {
        key: user.user_id,
        userId: user.user_id,
        name: displayName || "—",
        email: user.email || "—",
        loginCount: user.login_count ?? 0,
        lastLoginAt: user.last_login_at,
      }
    })
  }, [users, lang])

  const handleViewTimeline = (userId, e) => {
    e?.stopPropagation()
    if (userId) navigate(`/all-users?userId=${userId}`)
  }

  const columns = [
    {
      title: lang === "ar" ? "الاسم" : "Name",
      dataIndex: "name",
      key: "name",
      width: 200,
      ellipsis: true,
      render: (text) => (
        <Space>
          <Avatar
            src={`https://ui-avatars.com/api/?name=${encodeURIComponent(
              text,
            )}&background=308AA3&color=fff`}
          />
          <span style={{ fontWeight: 500, color: "#101828" }}>{text}</span>
        </Space>
      ),
    },
    {
      title: lang === "ar" ? "البريد الإلكتروني" : "Email",
      dataIndex: "email",
      key: "email",
      width: 240,
      ellipsis: true,
      render: (email) => (
        <span style={{ color: "#4a5565", fontSize: "13px" }}>{email}</span>
      ),
    },
    {
      title: lang === "ar" ? "عدد تسجيلات الدخول" : "Login Count",
      dataIndex: "loginCount",
      key: "loginCount",
      width: 120,
      render: (count) => (
        <span style={{ fontWeight: 600, color: "#308AA3" }}>{count}</span>
      ),
    },
    {
      title: lang === "ar" ? "آخر تسجيل دخول" : "Last Login",
      dataIndex: "lastLoginAt",
      key: "lastLoginAt",
      width: 180,
      render: (value) =>
        value ? (
          <span style={{ color: "#4a5565", fontSize: "13px" }}>
            {dayjs(value).format("MMM D, YYYY HH:mm")}
          </span>
        ) : (
          "—"
        ),
    },
    {
      title: lang === "ar" ? "الإجراء" : "Action",
      key: "action",
      width: 100,
      align: "center",
      className: "admin-login-analytics__action-col",
      onHeaderCell: () => ({ className: "admin-login-analytics__action-col" }),
      onCell: () => ({ className: "admin-login-analytics__action-col" }),
      render: (_, record) => (
        <div className="admin-login-analytics__action-cell">
          <Button
            className="custom-view-btn"
            type="primary"
            style={{
              backgroundColor: "#FFF4ED",
              borderColor: "#FFF4ED",
              color: "#FF6B2C",
              fontWeight: 400,
              display: "inline-flex",
              alignItems: "center",
              gap: "4px",
              height: "32px",
              padding: "0 12px",
              boxShadow: "none",
            }}
            onClick={(e) => handleViewTimeline(record.userId, e)}
          >
            {lang === "ar" ? "عرض" : "View"}
            <img src="/dashboard/vector-up.svg" alt="" />
          </Button>
        </div>
      ),
    },
  ]

  const showPagination = total > pageSize

  return (
    <section className="admin-login-analytics">
      <h2 className="admin-login-analytics__table-title">
        {lang === "ar" ? "تحليلات تسجيل دخول المستخدمين" : "Users Login Analytics"}
      </h2>

      <div className="admin-login-analytics__table-wrap">
        <div className="responsive-table-wrapper">
          <ConfigProvider direction={lang === "ar" ? "rtl" : "ltr"}>
            <Spin spinning={loading}>
              <Table
                columns={columns}
                dataSource={tableData}
                tableLayout="fixed"
                scroll={{ x: 840 }}
                pagination={
                  showPagination
                    ? {
                        current: page,
                        total,
                        pageSize,
                        showSizeChanger: false,
                        position: ["bottomRight"],
                        onChange: (nextPage) => setPage(nextPage),
                      }
                    : false
                }
                locale={{
                  emptyText:
                    lang === "ar" ? "لا يوجد مستخدمون" : "No users with logins",
                }}
                className="admin-login-analytics__table"
              />
            </Spin>
          </ConfigProvider>
        </div>
      </div>
    </section>
  )
}

export default AdminLoginAnalytics
